import client from './client';
import { AdminEmployeeRole } from '../interfaces/EmployeeInfoInterface';

/**
 * API client for the super-admin-only Admin Management page (Approval Access WP9).
 * Consumes the WP6 endpoints:
 *   GET/POST/PUT/PATCH /admin/adminEmployees   (Tab A — Admin Employees)
 *   GET               /admin/adminEmployeeRoles (role dropdown / filter / Tab B badge)
 *   GET               /admin/employees/active   (linked-employee name map + picker)
 *
 * Request/response shapes mirror the API repo exactly:
 *   db/adminEmployeeUtil.ts (AdminEmployeeListItem, AdminEmployeeWriteInput),
 *   db/adminEmployeeRoleUtil.ts (AdminEmployeeRoleWithUsage),
 *   db/employeeUtil.ts (EmployeeResponse).
 *
 * All mutating calls let axios reject on 4xx; components surface the server's
 * `message` via getApiErrorMessage() (inline errors in modals, toast/banner elsewhere).
 */

/** A roster row from GET /admin/adminEmployees (db/adminEmployeeUtil AdminEmployeeListItem). */
export interface AdminEmployeeListItem {
  adminEmployeeID: number;
  altEmployeeID: string | null;
  email: string;
  employeeID: number | null;
  active: boolean;
  name: { first: string; middle: string | null; last: string };
  /** Null = employee-only login: no admin role, linked employee required (TAS-667). */
  adminEmployeeRoleID: number | null;
  roleName: string | null;
  roleIsSuperAdmin: boolean;
  roleActive: boolean | null;
  delegationCount: number;
  /** The row is bound to a Microsoft account (AzureObjectID set, TAS-667). Optional
   *  so an older API (which omits it) still parses. */
  signedIn?: boolean;
}

/** Body for POST/PUT /admin/adminEmployees (db/adminEmployeeUtil AdminEmployeeWriteInput). */
export interface AdminEmployeeWriteInput {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  email: string;
  altEmployeeID?: string | null;
  employeeID?: number | null;
  /** Null = employee-only access; employeeID is then required (TAS-667). */
  adminEmployeeRoleID: number | null;
}

/** A role from GET /admin/adminEmployeeRoles (AdminEmployeeRole + active-admin in-use count). */
export interface AdminRoleWithUsage extends AdminEmployeeRole {
  inUseCount: number;
}

/** Minimal employee shape used by the linked-employee picker + name map (EmployeeResponse subset). */
export interface EmployeeLite {
  employeeID: number;
  altEmployeeID: string | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  active: boolean;
}

/** Active filter for the roster. 'all' omits the query param. */
export type AdminActiveFilter = boolean | 'all';

/**
 * GET /admin/adminEmployees. `active`: true = active only (default view), false =
 * inactive only, 'all' = no filter.
 */
export const getAdminEmployees = async (
  active: AdminActiveFilter = true,
): Promise<AdminEmployeeListItem[]> => {
  const params: Record<string, unknown> = {};
  if (active !== 'all') params.active = active;
  const response = await client.get('/admin/adminEmployees', { params });
  return response.data as AdminEmployeeListItem[];
};

/** POST /admin/adminEmployees. Returns the new adminEmployeeID. */
export const createAdminEmployee = async (
  input: AdminEmployeeWriteInput,
): Promise<number> => {
  const response = await client.post('/admin/adminEmployees', input);
  return response.data?.adminEmployeeID as number;
};

/** PUT /admin/adminEmployees/:id. */
export const updateAdminEmployee = async (
  adminEmployeeID: number,
  input: AdminEmployeeWriteInput,
): Promise<void> => {
  await client.put(`/admin/adminEmployees/${adminEmployeeID}`, input);
};

/** PATCH /admin/adminEmployees/:id/active — soft inactivate / reactivate. */
export const setAdminEmployeeActive = async (
  adminEmployeeID: number,
  active: boolean,
): Promise<void> => {
  await client.patch(`/admin/adminEmployees/${adminEmployeeID}/active`, { active });
};

/**
 * PATCH /admin/adminEmployees/:id/resetSignIn — super admin. Clears the Microsoft
 * account binding so the next sign-in with the row's email binds again (TAS-667).
 */
export const resetAdminEmployeeSignIn = async (adminEmployeeID: number): Promise<void> => {
  await client.patch(`/admin/adminEmployees/${adminEmployeeID}/resetSignIn`);
};

/** GET /admin/adminEmployeeRoles — every role + per-role active-admin in-use count. */
export const getAdminEmployeeRoles = async (): Promise<AdminRoleWithUsage[]> => {
  const response = await client.get('/admin/adminEmployeeRoles');
  return response.data as AdminRoleWithUsage[];
};

/**
 * GET /admin/employees — the full employee list, used to (a) resolve employeeID → name
 * in the roster's Linked Employee column (the roster endpoint returns only the id, and a
 * linked employee may be inactive) and (b) drive the linked-employee picker (callers
 * filter to `active`). The caller is always a super admin (Admin implies ViewAllEmployees),
 * so this is unscoped.
 */
export const getEmployeesForLookup = async (): Promise<EmployeeLite[]> => {
  const response = await client.get('/admin/employees');
  const rows = (response.data ?? []) as any[];
  return rows.map((e) => ({
    employeeID: e.employeeID,
    altEmployeeID: e.altEmployeeID ?? null,
    firstName: e.firstName,
    middleName: e.middleName ?? null,
    lastName: e.lastName,
    active: e.active === true,
  }));
};

/**
 * Pull the server's error `message` off a rejected axios error (400/403 carry a short
 * JSON `{ message }`). Falls back to a generic string. Used for inline errors + toasts.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const anyErr = error as any;
  const msg = anyErr?.response?.data?.message;
  if (typeof msg === 'string' && msg.trim() !== '') return msg;
  return fallback;
}

/** HTTP status of a rejected axios error, or undefined. */
export function getApiErrorStatus(error: unknown): number | undefined {
  return (error as any)?.response?.status;
}
